import { useCallback, useEffect } from 'react';

import { type CategoryTree } from '@/shared/category/type';

import { OTHER_PUBLISHER_ID } from '../constant';
import { type PublisherFormValues, type PublisherList } from '../types';

import { usePublisherNodeList } from './usePublisherNodeList';
import { useSelectCustomPublisher } from './useSelectCustomPublisher';
import { useSelectPublisher } from './useSelectPublisher';
import { useValidatePublisherCategory } from './useValidatePublisherCategory';

/**
 * 출판사 정보 관리 hook
 *
 * 출판사(publisher)와 커스텀 출판사(customPublisher) 선택을 관리합니다.
 * 선택된 학교 타입과 학년에 따라 출판사 목록이 필터링됩니다.
 */
export const usePublisherCategorySelector = ({
  publisherFormValues,
  publishers,
  selectedSchoolGroupId,
  selectedGradeId,
  schoolInfoTree,
}: {
  publisherFormValues: PublisherFormValues;
  publishers?: PublisherList;
  selectedSchoolGroupId?: string;
  selectedGradeId?: string;
  schoolInfoTree: CategoryTree;
}) => {
  const publisherNodes = usePublisherNodeList({
    publishers,
    selectedSchoolTypeId: selectedSchoolGroupId,
    selectedGradeId,
    schoolInfoTree,
  });

  const [selectedPublisherNode, selectPublisher] = useSelectPublisher({
    publisherNodes,
    publisherFormValues,
  });

  const [selectedCustomPublisher, setSelectedCustomPublisher] =
    useSelectCustomPublisher({
      publisherFormValues,
    });

  // 커스텀 출판사 입력
  const setCustomPublisher = (value?: string) => {
    setSelectedCustomPublisher(value);
  };

  useEffect(() => {
    // 출판사 '기타' 선택 시, custom publisher 초기화
    if (selectedPublisherNode?.nodeId !== OTHER_PUBLISHER_ID) {
      setSelectedCustomPublisher(undefined);
    }
  }, [selectedPublisherNode?.nodeId, setSelectedCustomPublisher]); // FIX: setter 의존성 추가

  const {
    errors,
    validate: _validate,
    setError,
    clearError,
  } = useValidatePublisherCategory();

  const validate = useCallback((): boolean => {
    return _validate({
      selectedPublisherNode,
      selectedCustomPublisher,
    });
  }, [selectedPublisherNode, selectedCustomPublisher, _validate]);

  return {
    // 상태
    selectedPublisher: selectedPublisherNode,
    selectedCustomPublisher,

    // 파생 데이터
    publishers: publisherNodes,

    // 액션
    setPublisher: selectPublisher,
    setCustomPublisher,

    // 에러
    errors,
    setError,
    clearError,

    // 유효성 검사
    validate,
  };
};
